import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { PageHeader } from "shared/components/layouts/PageHeader";
import { List } from "iconoir-react";
import { BundleHeader } from "shared/components/app/BundleHeader";
import { EmptyState } from "shared/components/root/EmptyState";
import { useWidgetItemsEvents } from "./useWidgetItemsEvents";
import { useVirtualizer } from "@tanstack/react-virtual";
import { EventWithOccurrences } from "gql/generated";
import { WidgetItemEventCard } from "./WidgetItemEventCard";
import { EventCreateEditModal } from "shared/components/organisms/EventCreationAndEditing/EventCreateEditModal";

export const WidgetItemsEvents = ({
  virtualScrollRef,
}: {
  virtualScrollRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const { t } = useTranslation();

  const {
    eventsLoading,
    filteredEvents,
    itemHeight,
    itemSpacing,
    virtualizedHeight,
    numEvents,
    searchValue,
    handleEventDetailsClick,
    onSearchValueChange,
    isEventDetailsModalOpen,
    selectedEventId,
    handleEventDetailsModalClose,
    dbUser,
    loggedInAs,
    organizationId,
    updateOrAddEvent,
    removeEvent,
  } = useWidgetItemsEvents();

  // Setup the virtualizer
  const virtualizer = useVirtualizer({
    count: filteredEvents.length,
    getScrollElement: () => virtualScrollRef.current,
    estimateSize: () => itemHeight + itemSpacing,
    overscan: 15,
    scrollMargin: virtualScrollRef.current?.offsetTop ?? 0,
  });

  return (
    <>
      <Flex flexDir={"column"} gap={"300"}>
        <PageHeader
          title={t("widgets:DETAILS.TABS.WIDGET_ITEMS")}
          description={t(
            "widgets:DETAILS.TABS.DESCRIPTIONS.WIDGET_ITEMS_EVENT",
          )}
          Icon={List}
        />

        <BundleHeader
          searchProps={{
            value: searchValue,
            onValueChange: onSearchValueChange,
          }}
          count={numEvents}
        />

        <Flex
          flexDir="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap="400"
          marginTop="400"
        >
          {/* We're using a regular div here so we can easily and dynamically set the style */}
          <div
            style={{
              position: "relative",
              width: "100%",
              height: `${virtualizedHeight}px`,
            }}
          >
            {numEvents < 1 && !eventsLoading && (
              <EmptyState text={t("widgets:DETAILS.EMPTY_EVENTS")} />
            )}
            {virtualizer.getVirtualItems().map((item, index) => (
              <div
                key={item.key}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${itemHeight}px`,
                  maxHeight: `${itemHeight}px`,
                  transform: `translateY(${itemHeight * index + itemSpacing * index}px)`,
                }}
              >
                <WidgetItemEventCard
                  event={filteredEvents[item.index] as EventWithOccurrences}
                  handleEventDetailsClick={handleEventDetailsClick}
                />
              </div>
            ))}
          </div>
        </Flex>
      </Flex>
      <EventCreateEditModal
        isOpen={isEventDetailsModalOpen}
        userEmailAddress={dbUser?.email ?? ""}
        userType={loggedInAs?.type ?? "PUBLIC"}
        initialMode={"edit"}
        defaultOrganizationId={organizationId}
        defaultOperatorEntityIds={[]}
        eventId={selectedEventId}
        onClose={handleEventDetailsModalClose}
        onEventUpdated={updateOrAddEvent}
        onEventDeleted={removeEvent}
        hideMenuButtons={true}
      />
    </>
  );
};
